import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { Wallet, Smartphone, ShieldCheck, Loader2, Percent } from "lucide-react";

const METHODS = [
  { key: "tft", label: "Portefeuille TFT", desc: "Paiement instantané via solde TFT (avec cashback 2%)", Icon: Wallet, color: "#FACC15" },
  { key: "mtn", label: "MTN Mobile Money", desc: "Recharge du portefeuille TFT via MTN", Icon: Smartphone, color: "#F59E0B" },
  { key: "orange", label: "Orange Money", desc: "Recharge du portefeuille TFT via Orange", Icon: Smartphone, color: "#F97316" },
];

export default function PaymentMethodsPanel() {
  const [config, setConfig] = useState({ tft: true, mtn: true, orange: true, cashback_rate: 0.02 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/passenger-payment-methods");
      setConfig({ tft: !!data.tft, mtn: !!data.mtn, orange: !!data.orange, cashback_rate: data.cashback_rate || 0.02 });
    } catch (e) {
      toast.error("Impossible de charger la configuration", { description: e.response?.data?.detail || "Erreur" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (key, value) => {
    setSaving(key);
    const prev = config[key];
    setConfig((c) => ({ ...c, [key]: value }));
    try {
      await api.put("/admin/passenger-payment-methods", { [key]: value });
      toast.success(`${METHODS.find((m) => m.key === key).label} ${value ? "activé" : "désactivé"}`);
    } catch (e) {
      setConfig((c) => ({ ...c, [key]: prev }));
      toast.error("Échec de la mise à jour", { description: e.response?.data?.detail || "Erreur" });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-payment-methods-panel">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#1E3A5F] rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#FACC15]" />
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Moyens de paiement passagers</h2>
        </div>
        <p className="text-sm text-white/70">
          Activez ou désactivez globalement les moyens de paiement disponibles pour les passagers. Les méthodes désactivées sont masquées dans l&apos;interface.
        </p>
      </div>

      {/* Cashback info */}
      <div className="bg-[#FACC15]/10 border border-[#FACC15]/30 rounded-xl p-4 flex items-start gap-3" data-testid="cashback-info">
        <Percent className="w-4 h-4 text-[#0A2540] mt-0.5 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-[#0A2540]">Cashback TFT actif</p>
          <p className="text-xs text-[#0A2540]/80 mt-0.5">
            Les passagers reçoivent <strong>{(config.cashback_rate * 100).toFixed(0)}%</strong> de cashback en TFT sur chaque réservation payée via leur portefeuille. Le cashback est récupéré automatiquement si la réservation est annulée.
          </p>
        </div>
      </div>

      {/* Toggle cards */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-[#0A2540] animate-spin" /></div>
      ) : (
        <div className="space-y-3">
          {METHODS.map(({ key, label, desc, Icon, color }) => (
            <div
              key={key}
              className={`bg-white rounded-2xl border-2 p-4 flex items-center gap-4 transition-all ${config[key] ? "border-[#10B981]/40" : "border-[#CBD5E1]"}`}
              data-testid={`payment-method-row-${key}`}
            >
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}20` }}>
                <Icon className="w-6 h-6" style={{ color }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>{label}</h3>
                  <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${config[key] ? "bg-[#10B981]/10 text-[#10B981]" : "bg-[#94A3B8]/20 text-[#64748B]"}`}>
                    {config[key] ? "Actif" : "Désactivé"}
                  </span>
                </div>
                <p className="text-xs text-[#64748B] mt-0.5">{desc}</p>
              </div>
              <Switch
                checked={config[key]}
                onCheckedChange={(v) => toggle(key, v)}
                disabled={saving === key}
                data-testid={`payment-method-toggle-${key}`}
                aria-label={`Activer ${label}`}
              />
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#0A2540]/5 border border-[#CBD5E1] rounded-xl p-4">
        <p className="text-xs text-[#64748B]">
          <strong className="text-[#0A2540]">Astuce :</strong> pour désactiver un moyen de paiement pour un passager spécifique uniquement (et non pour tous), allez dans l&apos;onglet <strong>Utilisateurs</strong>, ouvrez le profil du passager et configurez ses moyens autorisés.
        </p>
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} data-testid="reload-payment-methods-btn">Actualiser</Button>
      </div>
    </div>
  );
}
